/**
 * Responsive Design Helper Utilities
 * Use these utilities to test and debug responsive layouts
 */

import { getDeviceType, breakpoints } from '../config/breakpoints';

/**
 * Get current viewport dimensions and device info
 * Useful for debugging responsive issues
 */
export const getViewportInfo = () => {
  if (typeof window === 'undefined') {
    return {
      width: 0,
      height: 0,
      deviceType: 'unknown',
      orientation: 'unknown',
      pixelRatio: 1,
    };
  }

  return {
    width: window.innerWidth,
    height: window.innerHeight,
    deviceType: getDeviceType(),
    orientation: window.innerWidth > window.innerHeight ? 'landscape' : 'portrait',
    pixelRatio: window.devicePixelRatio || 1,
    breakpoint: {
      phone: window.innerWidth <= breakpoints.phone.max,
      tablet: window.innerWidth >= breakpoints.tablet.min && window.innerWidth <= breakpoints.tablet.max,
      laptop: window.innerWidth >= breakpoints.laptop.min && window.innerWidth <= breakpoints.laptop.max,
      desktop: window.innerWidth >= breakpoints.desktop.min,
    },
  };
};

/**
 * Log current viewport info to console
 * Useful for debugging during testing
 */
export const logViewportInfo = () => {
  const info = getViewportInfo();
  console.group('📱 Viewport Info');
  console.log('Width:', info.width, 'px');
  console.log('Height:', info.height, 'px');
  console.log('Device Type:', info.deviceType);
  console.log('Orientation:', info.orientation);
  console.log('Pixel Ratio:', info.pixelRatio);
  console.log('Active Breakpoints:', info.breakpoint);
  console.groupEnd();
  return info;
};

/**
 * Add a visual indicator showing current device type
 * Useful for testing - shows in top-right corner
 */
export const showDeviceIndicator = () => {
  if (typeof document === 'undefined') return;

  // Remove existing indicator if present
  const existing = document.getElementById('device-indicator');
  if (existing) existing.remove();

  const indicator = document.createElement('div');
  indicator.id = 'device-indicator';
  indicator.style.cssText = `
    position: fixed;
    top: 10px;
    right: 10px;
    background: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 8px 12px;
    border-radius: 4px;
    font-family: monospace;
    font-size: 12px;
    z-index: 9999;
    pointer-events: none;
    border: 2px solid #4a9eff;
  `;

  const updateIndicator = () => {
    const info = getViewportInfo();
    indicator.innerHTML = `
      <div><strong>${info.deviceType.toUpperCase()}</strong></div>
      <div>${info.width} × ${info.height}px</div>
      <div>${info.orientation}</div>
    `;
  };

  updateIndicator();
  window.addEventListener('resize', updateIndicator);
  document.body.appendChild(indicator);

  return () => {
    window.removeEventListener('resize', updateIndicator);
    indicator.remove();
  };
};

/**
 * Check if element has responsive issues
 * Looks for common problems like fixed widths, overflow, etc.
 */
export const checkElementResponsiveness = (element) => {
  if (!element || typeof window === 'undefined') return null;

  const styles = window.getComputedStyle(element);
  const issues = [];

  // Check for fixed widths that might break on small screens
  const width = styles.width;
  if (width && !width.includes('%') && !width.includes('vw') && !width.includes('auto') && !width.includes('min') && !width.includes('max')) {
    const widthValue = parseInt(width);
    if (widthValue > 0 && widthValue < window.innerWidth * 0.9) {
      issues.push({
        type: 'fixed-width',
        message: `Fixed width: ${width} might not be responsive`,
        severity: 'medium',
      });
    }
  }

  // Check for overflow issues
  if (styles.overflow === 'hidden' && (element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight)) {
    issues.push({
      type: 'overflow-hidden',
      message: 'Content might be clipped due to overflow: hidden',
      severity: 'high',
    });
  }

  // Check for very small touch targets
  const minTouchSize = 44; // iOS/Android minimum
  const rect = element.getBoundingClientRect();
  if (rect.width < minTouchSize || rect.height < minTouchSize) {
    if (element.tagName === 'BUTTON' || element.tagName === 'A' || element.onclick) {
      issues.push({
        type: 'small-touch-target',
        message: `Touch target might be too small: ${Math.round(rect.width)} × ${Math.round(rect.height)}px`,
        severity: 'medium',
      });
    }
  }

  return issues.length > 0 ? issues : null;
};

/**
 * Test all breakpoints programmatically
 * Useful for automated testing
 */
export const testBreakpoints = () => {
  const testSizes = [
    { name: 'Phone Portrait', width: 375, height: 667 },
    { name: 'Phone Landscape', width: 667, height: 375 },
    { name: 'Tablet Portrait', width: 768, height: 1024 },
    { name: 'Tablet Landscape', width: 1024, height: 768 },
    { name: 'Laptop', width: 1366, height: 768 },
    { name: 'Desktop', width: 1920, height: 1080 },
  ];

  return testSizes.map(({ name, width, height }) => {
    // Simulate viewport (for testing purposes)
    const deviceType = width <= breakpoints.phone.max ? 'phone' :
                      width <= breakpoints.tablet.max ? 'tablet' :
                      width <= breakpoints.laptop.max ? 'laptop' : 'desktop';

    return {
      name,
      width,
      height,
      deviceType,
      orientation: width > height ? 'landscape' : 'portrait',
    };
  });
};






